import React from 'react';
import { Box, Typography, Chip, Stack } from '@mui/material';

/**
 * SVG sketch of rectangular concrete section + rebars.
 * Section (y,z): y horizontal 0..b, z vertical 0 bottom .. h top (matches backend).
 */
function barCentersX(n, xStart, innerW, rPx) {
  if (n <= 0) return [];
  if (n === 1) return [xStart + innerW / 2];
  const step = (innerW - 2 * rPx) / (n - 1);
  return Array.from({ length: n }, (_, i) => xStart + rPx + i * step);
}

export default function ConcreteSectionSketch({ section }) {
  if (!section?.width_m || !section?.height_m || !section.bars?.length) return null;

  const W = section.width_m;
  const H = section.height_m;
  const cover = section.cover_m ?? 0.03;
  const margin = 10;
  const maxPx = 280;
  const s = maxPx / Math.max(W, H, 1e-6);
  const bw = W * s;
  const bh = H * s;
  const vbW = bw + 2 * margin;
  const vbH = bh + 2 * margin;

  const bars = section.bars;
  const allNearZeroY = bars.every((b) => Math.abs(b.y_m) < 1e-4);
  const useSpread = allNearZeroY;

  const innerW = Math.max(bw - 2 * cover * s, 1e-6);
  const xStart = margin + cover * s;

  const rList = bars.map((b) => Math.max(2, ((b.diam_mm ?? 16) / 1000) * s * 0.5));
  const rMax = Math.max(...rList, 2);

  const layerTol = 0.002;
  const layers = [];
  bars.forEach((b) => {
    const z = b.z_m;
    let layer = layers.find((L) => Math.abs(L.z - z) < layerTol);
    if (!layer) {
      layer = { z, items: [] };
      layers.push(layer);
    }
    layer.items.push(b);
  });
  layers.sort((a, b) => a.z - b.z);

  const barPix = [];
  layers.forEach((layer) => {
    const nL = layer.items.length;
    const xsLayer = useSpread ? barCentersX(nL, xStart, innerW, rMax) : null;
    layer.items.forEach((b, j) => {
      const r = Math.max(2, ((b.diam_mm ?? 16) / 1000) * s * 0.5);
      const ySvg = margin + bh - b.z_m * s;
      const xSvg = useSpread ? xsLayer[j] : margin + b.y_m * s;
      barPix.push({ ...b, xSvg, ySvg, r });
    });
  });

  const ro = section.rebar_optimization;
  let modeChip = null;
  if (section.rebar_optimized) {
    modeChip = (
      <Chip
        size="small"
        color="primary"
        label={
          <span>
            Rebar auto-sized (linear sag + hog M<sub>Ed</sub>)
          </span>
        }
      />
    );
  } else if (ro?.mode === 'manual') {
    modeChip = <Chip size="small" variant="outlined" label="Manual bar count / diameter" />;
  } else if (ro?.mode === 'custom_layers') {
    modeChip = <Chip size="small" variant="outlined" label="Custom bar layout" />;
  } else if (ro?.mode === 'nonlinear_advisory') {
    modeChip = (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        label="Advisory rebar sizing (nonlinear |MEd|)"
      />
    );
  } else if (ro?.mode === 'preset') {
    modeChip = <Chip size="small" variant="outlined" label="Preset bars (nonlinear)" />;
  } else if (ro?.error) {
    modeChip = <Chip size="small" color="warning" label="Auto-size failed — initial bars" />;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Concrete cross-section
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1 }}>
        {modeChip}
        {ro?.sagging || ro?.hogging ? (
          <Typography variant="caption" color="text.secondary" component="span">
            {ro.sagging && (
              <span>
                Sag: Ø{Number(ro.sagging.diameter_mm).toFixed(0)} × {ro.sagging.count}
              </span>
            )}
            {ro.sagging && ro.hogging ? ' · ' : null}
            {ro.hogging && (
              <span>
                Hog: Ø{Number(ro.hogging.diameter_mm).toFixed(0)} × {ro.hogging.count}
              </span>
            )}
          </Typography>
        ) : (
          ro?.diameter_mm != null &&
          ro?.count != null && (
            <Typography variant="caption" color="text.secondary">
              Ø{Number(ro.diameter_mm).toFixed(0)} mm × {ro.count}
            </Typography>
          )
        )}
      </Stack>
      {ro?.note && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {ro.note}
        </Typography>
      )}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'inline-block', bgcolor: 'background.paper' }}>
        <svg
          viewBox={`0 0 ${vbW} ${vbH}`}
          width={vbW}
          height={vbH}
          style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
          aria-label="Concrete cross-section with reinforcement"
        >
          <rect x={margin} y={margin} width={bw} height={bh} fill="#e0e0e0" stroke="#424242" strokeWidth={1.2} />
          <rect
            x={margin + cover * s}
            y={margin + cover * s}
            width={bw - 2 * cover * s}
            height={bh - 2 * cover * s}
            fill="none"
            stroke="#757575"
            strokeDasharray="5 4"
            strokeWidth={0.9}
          />
          {barPix.map((bp, i) => (
            <circle
              key={i}
              cx={bp.xSvg}
              cy={bp.ySvg}
              r={bp.r}
              fill={bp.compression ? '#b71c1c' : '#0d47a1'}
              stroke="#212121"
              strokeWidth={0.6}
            />
          ))}
          <text x={margin + 4} y={margin + bh + 14} fontSize={10} fill="#616161">
            b = {(W * 1000).toFixed(0)} mm
          </text>
          <text x={margin + bw - 2} y={margin + 12} fontSize={10} fill="#616161" textAnchor="end">
            h = {(H * 1000).toFixed(0)} mm
          </text>
        </svg>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        Blue: tension steel; red: compression (if any). Bars at y ≈ 0 are spread horizontally per layer for
        visibility.
      </Typography>
    </Box>
  );
}
